import {Injectable} from "@angular/core";
import * as moment from "moment";
import {VISIT_PLAN_AUDIT_LOG_FILTERS} from "../../../scheduling-project/angular/constants/scheduling-project.constant";

export interface AuditLogEntry {
  id: string;
  siteId?: string;
  correlationId?: string;
  groupId?: string;
  action: string;
  operation: string;
  domains?: string[];
  status: string;
  actor?: {
    name?: string;
    source?: string;
  };
  requestData?: {
    ipAddress?: string;
    userAgent?: string;
    apiRequest?: string;
    apiMethod?: string;
  };
  metadata?: {
    [key: string]: string;
  };
  performedAt: number;
  createdAt: number;
  updatedAt: number;
}

export enum AuditLogStatus {
  Success = 'SUCCESS',
  Failed = 'FAILURE'
}

export interface VisitPlanLog {
  id: string;
  performedAt: number;
  formattedDate: string;
  by: string;
  action: string;
  agentIds: string[];
  agentsCount?: number;
  accountIds: string[];
  accountsCount?: number;
  forDays: string[];
  forDaysCount?: number;
  currentState: string;
  status: AuditLogStatus | string;
}

const NO_VALUE_PLACEHOLDER: string = '---';

@Injectable({ providedIn: 'root' })
export class AuditLogParserService {
  public getVisitPlanLogs(logs: AuditLogEntry[]): VisitPlanLog[] {
    if (!logs) return [];
    return logs.map(log => ({
      id: log.id,
      performedAt: log.performedAt,
      formattedDate: this.formatDate(log.performedAt),
      by: log.actor?.name || log.actor?.source || NO_VALUE_PLACEHOLDER,
      action: this.getVisitPlanActionName(log),
      agentIds: this.getVisitPlanEntityIds(log, 'agents', ['agent', 'agentId']),
      agentsCount: this.getVisitPlanEntityCount(log, 'agents'),
      accountIds: this.getVisitPlanEntityIds(log, 'accounts', ['account', 'accountId']),
      accountsCount: this.getVisitPlanEntityCount(log, 'accounts'),
      forDays: this.getVisitPlanForDays(log),
      forDaysCount: this.getVisitPlanEntityCount(log, 'dayIds'),
      currentState: this.deriveVisitPlanCurrentState(log),
      status: log.status
    }));
  }

  private getVisitPlanEntityIds(log: AuditLogEntry, key: string, legacyKeys: string[]): string[] {
    const raw = log.metadata?.[key];
    if (raw) {
      return raw.split(',').map(id => id.trim()).filter(Boolean);
    }
    const legacyId = legacyKeys.map(legacyKey => log.metadata?.[legacyKey]).find(Boolean);
    return legacyId ? [legacyId] : [];
  }

  /**
   * The server stores a collection metadata entry inline only up to its element limit; a larger
   * collection is filed as '<key>Count' with no ids at all. Bulk operations therefore report just
   * how many entities they touched.
   */
  private getVisitPlanEntityCount(log: AuditLogEntry, key: string): number | undefined {
    const raw = log.metadata?.[`${key}Count`];
    if (!raw) return undefined;
    const count = Number(raw);
    return isNaN(count) ? undefined : count;
  }

  private getVisitPlanForDays(log: AuditLogEntry): string[] {
    if (!log.metadata) return [];
    if (log.metadata.dayIds) {
      const parts = log.metadata.dayIds.split(',');
      return parts.map(this.formatSingleDayId.bind(this));
    } else if (log.metadata.day) {
      return [this.formatDay(log.metadata.day)];
    }
    return [];
  }

  public formatDate(timestamp: number): string {
    if (!timestamp) return '';
    return moment(timestamp).format("MM/DD/YY HH:mm");
  }

  public formatDay(dayStr: string): string {
    if (!dayStr) return NO_VALUE_PLACEHOLDER;
    const m = moment(dayStr, "YYYY-MM-DD");
    if (!m.isValid()) return dayStr;
    return m.format("dddd MM/DD/YY");
  }

  public formatDayIds(dayIdsStr: string): string {
    if (!dayIdsStr) return NO_VALUE_PLACEHOLDER;
    const parts = dayIdsStr.split(',');
    const formattedDays = parts.map(this.formatSingleDayId.bind(this));
    return formattedDays.join(', ');
  }

  public formatState(stateStr: string): string {
    if (!stateStr || stateStr === 'null') return NO_VALUE_PLACEHOLDER;
    try {
      const obj = JSON.parse(stateStr);
      return Object.entries(obj)
        .map(([key, val]) => `${this.camelToWords(key)}: ${this.stringifyVal(val)}`)
        .join(', ');
    } catch (e) {
      return stateStr;
    }
  }

  private getVisitPlanActionName(log: AuditLogEntry): string {
    const key = log.operation || log.action || NO_VALUE_PLACEHOLDER;
    const filter = VISIT_PLAN_AUDIT_LOG_FILTERS.find(f => f === key);

    return filter || key || NO_VALUE_PLACEHOLDER;
  }

  private deriveVisitPlanCurrentState(log: AuditLogEntry): string {
    let state = '';
    if (log.status) {
      state = `Status: ${log.status}`;
    }

    const opDetails = this.getVisitPlanOperationDetails(log);
    if (opDetails) {
      state = state ? `${state} (${opDetails})` : opDetails;
    }

    return state || NO_VALUE_PLACEHOLDER;
  }

  private getVisitPlanOperationDetails(log: AuditLogEntry): string {
    if (!log.metadata) return '';
    const operation = log.operation;
    switch (operation) {
      case 'OPTIMIZE':
      case 'UPDATE_VISIT_SCHEDULE':
        return `Schedule: ${log.metadata.visitScheduleId || NO_VALUE_PLACEHOLDER}`;
      case 'RECALCULATE':
        return 'Recalculated';
      case 'PUBLISH':
        return 'Published';
      case 'AUTO_DISPATCH':
        return 'Dispatched';
      case 'OPTIMIZE_ROUTE':
        return 'Route Optimized';
      default:
        return '';
    }
  }

  private camelToWords(str: string): string {
    const result = str.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  private stringifyVal(val: any): string {
    if (Array.isArray(val)) {
      return `[${val.join(', ')}]`;
    }
    return String(val);
  }

  private formatSingleDayId(part: string): string {
    const datePart = part.split('_')[0];
    const m = moment(datePart, "DD-MM-YYYY");

    if (m.isValid()) {
      return m.format("dddd MM/DD/YY");
    }

    const mFallback = moment(part, "YYYY-MM-DD");
    if (mFallback.isValid()) {
      return mFallback.format("dddd MM/DD/YY");
    }

    return part;
  }
}
